import clsx from 'clsx';
import { nanoid } from 'nanoid';
import { FunctionComponent, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { KeyCode, logAnalyticsEvent, noopFunction } from '@vestico/utils';

import { userDataCompleted } from '@vestico/model';

import { Button, Footer, GlobalsContext, Horizontal, UserContext, Vertical } from '@vestico/shared';

import { getIsRTL } from '@vestico/i18n';

import { ProductContext } from '../MainContext';
import style from './MainModalWindow.module.css';
import { MainWindow } from './MainWindow';

export interface MainModalWindowProps extends PropsWithChildren {
  isOpen: boolean;
  setOpen: (nopen: boolean) => void;
  goToPrevious?: () => void;
  goToNext?: () => void;
  buttonsDisabled?: boolean;
  hideReset?: boolean;
}

export interface MainModalWindowInternalProps extends MainModalWindowProps {
  iframeID?: string;
}

const BUTTON_SIZE = 24;
const BUTTON_STROKE_WIDTH = 2;
const BUTTON_COLOR = '#eeeeee';
const BUTTON_COLOR_DISABLED = '#565656';
const BOTTOM_CONTAINER_BUTTON_COLOR = '#ccc';

type KeyNavigation = 'prev' | 'next' | 'close' | 'tab';

export const MainModalWindow: FunctionComponent<MainModalWindowProps> = ({
  isOpen,
  setOpen,
  goToPrevious,
  goToNext,
  buttonsDisabled,
  children,
  hideReset,
}) => {
  const { stylesConfig } = useContext(GlobalsContext);
  const { useiFrame } = stylesConfig.stylesConfig;
  const [iFrameID] = useState(`vw-iframe-${nanoid()}`);

  const content = (
    <MainModalWindowInner
      isOpen={isOpen}
      setOpen={setOpen}
      goToPrevious={goToPrevious}
      goToNext={goToNext}
      buttonsDisabled={buttonsDisabled}
      iframeID={useiFrame ? iFrameID : undefined}
      hideReset={hideReset}
    >
      {children}
    </MainModalWindowInner>
  );

  const [frameProvider, setFrameProvider] = useState<typeof import('./FrameProvider') | undefined>(undefined);

  useEffect(() => {
    if (useiFrame && !frameProvider) {
      import('./FrameProvider')
        .then((component) => {
          setFrameProvider(component);
        })
        .catch((e) => {
          console.error(e);
          logAnalyticsEvent({ category: 'exception', event: 'get-frame-provider', name: e.message });
        });
    }
  }, [useiFrame, frameProvider]);

  if (!useiFrame) {
    return content;
  }

  return frameProvider ? frameProvider.FrameProvider({ iFrameID, isOpen, children: content }) : null;
};

export const MainModalWindowInner = ({
  isOpen,
  setOpen,
  goToPrevious,
  goToNext,
  buttonsDisabled,
  iframeID,
  hideReset,
  children,
}: MainModalWindowInternalProps) => {
  const { useMobileLayout, stylesConfig } = useContext(GlobalsContext);
  const { product } = useContext(ProductContext);
  const { useiFrame, customPrivacyURL } = stylesConfig.stylesConfig;
  const { user, reloadUser } = useContext(UserContext);
  const { t } = useTranslation();

  const toggleCarouselOpen = () => setOpen(!isOpen);

  const buttonContentColor = buttonsDisabled ? BUTTON_COLOR_DISABLED : BUTTON_COLOR;

  const isRTL = getIsRTL();
  // Outer button containers
  const topContainer = !useMobileLayout ? (
    <div className={clsx(style.topBackground, { [style.rtl]: isRTL })}>
      <Button
        roundedBlur
        size="small"
        onClick={toggleCarouselOpen}
        disabled={buttonsDisabled}
        ariaLabel="close"
        color="#fff"
        content={
          <div className={clsx(style.close)}>
            <X color={buttonContentColor} size={BUTTON_SIZE} strokeWidth={BUTTON_STROKE_WIDTH} />
          </div>
        }
      />
    </div>
  ) : undefined;

  const getArrow = useCallback(
    (dir: 'next' | 'previous') => {
      const isChevronLeft = (dir === 'next' && isRTL) || (dir === 'previous' && !isRTL);
      if (isChevronLeft) {
        return (
          <div className={clsx(style.arrowLeft)}>
            <ChevronLeft color={buttonContentColor} size={BUTTON_SIZE} strokeWidth={BUTTON_STROKE_WIDTH} style={{ fill: 'none' }} />
          </div>
        );
      }
      return (
        <div className={clsx(style.arrowRight)}>
          <ChevronRight color={buttonContentColor} size={BUTTON_SIZE} strokeWidth={BUTTON_STROKE_WIDTH} style={{ fill: 'none' }} />
        </div>
      );
    },
    [isRTL, buttonContentColor]
  );

  const goToPreviousButton =
    goToPrevious && !useMobileLayout ? (
      <div className={clsx(style.leftBackground, { [style.rtl]: isRTL })}>
        <Button
          roundedBlur
          size="small"
          color="#fff"
          onClick={goToPrevious}
          disabled={buttonsDisabled}
          content={getArrow('previous')}
          ariaLabel="previous"
        />
      </div>
    ) : undefined;

  const goToNextButton =
    goToNext && !useMobileLayout ? (
      <div className={clsx(style.rightBackground, { [style.rtl]: isRTL })}>
        <Button roundedBlur size="small" color="#fff" onClick={goToNext} disabled={buttonsDisabled} content={getArrow('next')} ariaLabel="next" />
      </div>
    ) : undefined;

  const reset = useCallback(() => {
    if (user) {
      if (reloadUser) {
        reloadUser(true);
      }
    }
  }, [reloadUser, user]);

  const rightFooterActions =
    user && userDataCompleted(user, product?.matchingCategory, false, true) && !hideReset
      ? [
          // eslint-disable-next-line react/jsx-indent
          <div>
            <Button
              content={t('LOC_RESET_BODY_INFO_BUTTON')}
              onClick={reset}
              plain
              size="small"
              color={BOTTOM_CONTAINER_BUTTON_COLOR}
              disabled={buttonsDisabled}
            />
          </div>,
        ]
      : undefined;

  const bottomContainer = !useMobileLayout ? (
    <div className={clsx(style.bottomBackground)}>
      <Footer
        showBanner={false}
        showPrivacyLink={!buttonsDisabled}
        buttonColor={BOTTOM_CONTAINER_BUTTON_COLOR}
        rightActions={rightFooterActions}
        privacyPosition={{ horizontal: Horizontal.center, vertical: Vertical.top }}
        customPrivacyURL={customPrivacyURL}
      />
    </div>
  ) : undefined;

  const [pressedKey, setPressedKey] = useState<undefined | KeyNavigation>(undefined);
  const [focusWindow, setFocusWindow] = useState(false);

  useEffect(() => {
    if (!pressedKey) {
      return;
    }
    switch (pressedKey) {
      case 'close':
        setOpen(false);
        break;
      case 'next':
        if (isOpen && goToNext) {
          goToNext();
        }
        break;
      case 'prev':
        if (isOpen && goToPrevious) {
          goToPrevious();
        }
        break;
      case 'tab': {
        if (isOpen) {
          setFocusWindow(true);
        }
      }
    }
    setPressedKey(undefined);
  }, [goToNext, goToPrevious, isOpen, pressedKey, setOpen]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.code as KeyCode) {
        case KeyCode.tab: {
          if (focusWindow) {
            return;
          }
          e.preventDefault();
          setPressedKey('tab');
          break;
        }
        case KeyCode.arrowLeft:
          if (!useMobileLayout) {
            e.preventDefault();
            if (isRTL) {
              setPressedKey('next');
            } else {
              setPressedKey('prev');
            }
          }

          break;
        case KeyCode.arrowRight:
          if (!useMobileLayout) {
            e.preventDefault();
            if (isRTL) {
              setPressedKey('prev');
            } else {
              setPressedKey('next');
            }
          }

          break;
        case KeyCode.arrowUp:
          if (useMobileLayout) {
            e.preventDefault();
            setPressedKey('prev');
          }

          break;
        case KeyCode.arrowDown:
          if (useMobileLayout) {
            e.preventDefault();
            setPressedKey('next');
          }

          break;
        case KeyCode.escape:
          e.preventDefault();
          setPressedKey('close');
          break;
        // Do nothing
        case KeyCode.enter:
        default:
          break;
      }
    },
    [useMobileLayout, focusWindow, isRTL]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (!iframeID) {
      return noopFunction;
    }

    const frameContainer = document.getElementById(iframeID) as HTMLIFrameElement;
    const contentWindow = frameContainer?.contentWindow;
    if (!useiFrame || !frameContainer || !contentWindow) {
      return noopFunction;
    }

    contentWindow.addEventListener('keydown', handleKeyDown);
    return () => contentWindow.removeEventListener('keydown', handleKeyDown);
  }, [useiFrame, iframeID, handleKeyDown]);

  return (
    <MainWindow
      isOpen={isOpen}
      topBackgroundContent={topContainer}
      bottomBackgroundContent={bottomContainer}
      leftBackgroundContent={goToPreviousButton}
      rightBackgroundContent={goToNextButton}
      bordered={false}
      focusWindow={focusWindow}
    >
      {children}
    </MainWindow>
  );
};
